import { tva } from '@gluestack-ui/utils/nativewind-utils'
import React from 'react'
import { twMerge } from 'tailwind-merge'

import { useMeasuredWidth } from '../../useMeasuredWidth'
import { HStack } from '../hstack'
import { Pressable } from '../pressable'
import { Text } from '../text'
import { buildItems, ELLIPSIS, fittingButtons, PAGE_CELL_GAP } from './pageItems'

export type PaginationVariant = 'dark' | 'light'

export type PaginationProps = {
    /** Zero-based index of the current page. */
    currentPage: number
    totalPages: number
    /** Max number of page cells to show before collapsing with an ellipsis. */
    maxButtons?: number
    onChange: (page: number) => void
    prevLabel: string
    nextLabel: string
    variant?: PaginationVariant
    className?: string
    testID?: string
}

const navCell = tva({
    base: 'h-10 shrink-0 items-center justify-center rounded border-2 border-solid px-3',
    variants: {
        disabled: {
            true: 'border-border-subtle',
            false: 'border-action-default hover:bg-aos-bg-hover-layer',
        },
    },
})

const pageCell = tva({
    base: 'h-10 min-w-10 shrink-0 items-center justify-center rounded px-1',
    variants: {
        active: {
            true: 'bg-action-default',
            false: 'hover:bg-aos-bg-hover-layer',
        },
    },
})

const labelTone = (variant: PaginationVariant) => (variant === 'light' ? 'on-graphics' : 'default')

/**
 * Page navigation (Figma redesign): `action`-outlined Prev/Next, an
 * `action`-filled cell for the current page (its label is `on-primary` ink —
 * the fill is the tenant brand colour), plain cells otherwise, and
 * windowed ellipses. Prev/Next sit next to the numbers as one centred group at
 * the row's own gap — they are not pushed to the edges. The row always stays on
 * one line: the visible cell count is fitted to the width the nav cells leave
 * over, so narrow panels drop cells instead of wrapping onto a second line.
 * Presentational + controlled — page-window math is self-contained, i18n labels
 * are passed in.
 */
export const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    maxButtons = 9,
    onChange,
    prevLabel,
    nextLabel,
    variant = 'dark',
    className,
    testID,
}) => {
    const row = useMeasuredWidth()
    const prevNav = useMeasuredWidth()
    const nextNav = useMeasuredWidth()

    if (totalPages <= 1) {
        return null
    }

    // The group is centred rather than spread, so the cells get what the two nav
    // cells and the gaps around them leave over. Both are measured: their labels
    // are different strings per locale (et: Eelmine / Järgmine). `navCell` is
    // `shrink-0`, so neither measurement depends on the cell count and the fit
    // cannot oscillate; let them shrink and more cells would narrow them, freeing
    // room for yet more cells.
    const available = row.width - prevNav.width - nextNav.width - 2 * PAGE_CELL_GAP
    const items = buildItems(currentPage, totalPages, fittingButtons(available, maxButtons))
    const prevDisabled = currentPage === 0
    const nextDisabled = currentPage === totalPages - 1
    const tone = labelTone(variant)

    return (
        <HStack
            {...row.props}
            testID={testID}
            className={twMerge('w-full flex-nowrap items-center justify-center gap-x-6', className)}
        >
            <Pressable
                {...prevNav.props}
                onPress={() => !prevDisabled && onChange(currentPage - 1)}
                disabled={prevDisabled}
                accessibilityRole='button'
                className={navCell({ disabled: prevDisabled })}
            >
                <Text size='body' weight='semiBold' tone={prevDisabled ? 'muted' : tone}>
                    {prevLabel}
                </Text>
            </Pressable>

            <HStack className='min-w-0 shrink flex-nowrap items-center justify-center gap-x-6 overflow-hidden'>
                {items.map(item =>
                    item.type === 'ellipsis' ? (
                        <Pressable
                            key={item.key}
                            onPress={() => onChange(item.page)}
                            accessibilityRole='button'
                            className={pageCell({ active: false })}
                        >
                            <Text size='body' weight='medium' tone='muted'>
                                {ELLIPSIS}
                            </Text>
                        </Pressable>
                    ) : (
                        <Pressable
                            key={`page-${item.page}`}
                            onPress={() => onChange(item.page)}
                            accessibilityRole='button'
                            className={pageCell({ active: item.active })}
                        >
                            <Text
                                size='body'
                                weight='medium'
                                tone={item.active ? 'on-primary' : tone}
                            >
                                {item.label}
                            </Text>
                        </Pressable>
                    ),
                )}
            </HStack>

            <Pressable
                {...nextNav.props}
                onPress={() => !nextDisabled && onChange(currentPage + 1)}
                disabled={nextDisabled}
                accessibilityRole='button'
                className={navCell({ disabled: nextDisabled })}
            >
                <Text size='body' weight='semiBold' tone={nextDisabled ? 'muted' : tone}>
                    {nextLabel}
                </Text>
            </Pressable>
        </HStack>
    )
}

Pagination.displayName = 'Pagination'
